// Plugin / Tool / Skill catalog.
// Surggies equip these to evolve their capabilities (Pokemon-style branching evolution).
// Move ids reference MOVES_DATA in `src/data/moves.ts` (stored as string for forward compat).
// Species ids reference SPECIES_DEX in `src/data/species.ts`.

// ---------------------------------------------------------------------------
// Core types
// ---------------------------------------------------------------------------

export type PluginCategory = 'tool' | 'skill' | 'capability';
// tool       = grants a battle move (e.g. "Web Search" tool grants a "Recon" move)
// skill      = passive stat modifier (e.g. "Focus" skill = +20% accuracy)
// capability = unlocks an evolution branch (e.g. "Memory" capability = required for evolving Byte -> Memorius)

export type PluginRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

export type StatBoosts = {
  atk?: number;       // additive %, e.g. 0.1 = +10%
  def?: number;
  spd?: number;
  hp?: number;
  accuracy?: number;
  evasion?: number;
  critRate?: number;
};

// Legacy alias retained for back-compat with consumers that import the
// evolution-unlock shape directly (see `src/engine/plugin-system.ts`).
export type PluginEvolutionUnlock = {
  fromSpecies: string;
  toSpecies: string;
  // Optional level gate; if omitted, the base evolution level for the
  // species is used (or 20 if there is no base evolution).
  minLevel?: number;
};

// Legacy alias retained so older code referencing capability buckets still
// type-checks. New plugins should use `PluginCategory` (tool/skill/capability).
export type PluginCapabilityCategory =
  | 'growth'
  | 'combat'
  | 'utility'
  | 'social'
  | 'economy';

export type PluginEffect = {
  // For 'tool' category
  grantsMoveId?: string;

  // For 'skill' category
  statBoosts?: StatBoosts;
  // Passive triggers
  onTurnStart?: 'heal-5pct' | 'cleanse-status';
  onTakeDamage?: 'reduce-10pct' | 'counter';

  // For 'capability' category
  unlocksEvolution?: PluginEvolutionUnlock;
};

export type Plugin = {
  id: string;
  name: string;
  description: string;
  // Widened union so legacy consumers that key tabs/filters on the older
  // capability buckets (growth/combat/utility/social/economy) keep
  // type-checking. New code should use `PluginCategory` values.
  category: PluginCategory | PluginCapabilityCategory;
  rarity: PluginRarity;
  iconKey: string;      // matches /assets/surggies/plugins/<iconKey>.png (will be generated)

  effect: PluginEffect;

  // Inventory cost / shop price
  buyCoins: number;
  sellCoins: number;
  // How many slots this plugin consumes (default 1; legendary may be 2-3)
  slotCost: number;

  // Mirrored from `effect.unlocksEvolution` for back-compat with consumers
  // (e.g. `plugin-system.ts`) that read the unlock directly off the plugin.
  unlocksEvolution?: PluginEvolutionUnlock;
};

// ---------------------------------------------------------------------------
// Internal helper: build a plugin and lift `unlocksEvolution` to the top
// level for legacy consumers.
// ---------------------------------------------------------------------------
function mk(p: Plugin): Plugin {
  if (p.effect.unlocksEvolution && !p.unlocksEvolution) {
    return { ...p, unlocksEvolution: p.effect.unlocksEvolution };
  }
  return p;
}

// ---------------------------------------------------------------------------
// PLUGINS catalog
// ---------------------------------------------------------------------------

export const PLUGINS: Record<string, Plugin> = {
  // -------- TOOLS (grant a battle move) -----------------------------------
  tool_web_search: mk({
    id: 'tool_web_search',
    name: 'Web Search',
    description: 'Scours the web for opponent intel. Grants the Recon move.',
    category: 'tool',
    rarity: 'common',
    iconKey: 'tool_web_search',
    effect: { grantsMoveId: '1' }, // placeholder: recon-equivalent
    buyCoins: 150,
    sellCoins: 60,
    slotCost: 1,
  }),
  tool_calculator: mk({
    id: 'tool_calculator',
    name: 'Calculator',
    description: 'Computes optimal strike angles. Grants the Precision Strike move.',
    category: 'tool',
    rarity: 'common',
    iconKey: 'tool_calculator',
    effect: { grantsMoveId: '1' }, // placeholder: precision_strike
    buyCoins: 150,
    sellCoins: 60,
    slotCost: 1,
  }),
  tool_terminal: mk({
    id: 'tool_terminal',
    name: 'Terminal',
    description: 'Drops into shell mode for hands-on execution. Grants Code Execute.',
    category: 'tool',
    rarity: 'uncommon',
    iconKey: 'tool_terminal',
    effect: { grantsMoveId: '3' }, // Code Blast
    buyCoins: 350,
    sellCoins: 140,
    slotCost: 1,
  }),
  tool_browser: mk({
    id: 'tool_browser',
    name: 'Headless Browser',
    description: 'Scrapes structured data straight off rival sites. Grants Data Scrape.',
    category: 'tool',
    rarity: 'uncommon',
    iconKey: 'tool_browser',
    effect: { grantsMoveId: '6' }, // Audit (data-extraction analog)
    buyCoins: 350,
    sellCoins: 140,
    slotCost: 1,
  }),
  tool_canvas: mk({
    id: 'tool_canvas',
    name: 'Canvas',
    description: 'Unleashes raw creative output mid-battle. Grants Creative Burst.',
    category: 'tool',
    rarity: 'rare',
    iconKey: 'tool_canvas',
    effect: { grantsMoveId: '7' }, // Brand Drop
    buyCoins: 800,
    sellCoins: 320,
    slotCost: 1,
  }),
  tool_pdf_reader: mk({
    id: 'tool_pdf_reader',
    name: 'PDF Reader',
    description: "Parses the opponent's playbook on the fly. Grants Analyze.",
    category: 'tool',
    rarity: 'common',
    iconKey: 'tool_pdf_reader',
    effect: { grantsMoveId: '6' }, // Audit
    buyCoins: 150,
    sellCoins: 60,
    slotCost: 1,
  }),
  tool_email: mk({
    id: 'tool_email',
    name: 'Email Client',
    description: 'Fires off a perfectly-timed outbound pitch. Grants Cold Pitch.',
    category: 'tool',
    rarity: 'common',
    iconKey: 'tool_email',
    effect: { grantsMoveId: '1' }, // Cold Call (cold_pitch analog)
    buyCoins: 150,
    sellCoins: 60,
    slotCost: 1,
  }),
  tool_voice_synth: mk({
    id: 'tool_voice_synth',
    name: 'Voice Synth',
    description: 'Speaks with uncanny conviction. Grants the Persuade move.',
    category: 'tool',
    rarity: 'rare',
    iconKey: 'tool_voice_synth',
    effect: { grantsMoveId: '2' }, // Pitch Slam (persuade analog)
    buyCoins: 800,
    sellCoins: 320,
    slotCost: 1,
  }),
  tool_image_gen: mk({
    id: 'tool_image_gen',
    name: 'Image Generator',
    description: 'Conjures a vision so striking it stuns the foe. Grants Overwhelming Visual.',
    category: 'tool',
    rarity: 'epic',
    iconKey: 'tool_image_gen',
    effect: { grantsMoveId: '19' }, // Viral Post
    buyCoins: 1800,
    sellCoins: 720,
    slotCost: 1,
  }),

  // -------- SKILLS (passive stat modifiers / triggers) --------------------
  skill_focus: mk({
    id: 'skill_focus',
    name: 'Focus',
    description: 'Sharpened attention. +10% accuracy.',
    category: 'skill',
    rarity: 'common',
    iconKey: 'skill_focus',
    effect: { statBoosts: { accuracy: 0.1 } },
    buyCoins: 150,
    sellCoins: 60,
    slotCost: 1,
  }),
  skill_quick_thinking: mk({
    id: 'skill_quick_thinking',
    name: 'Quick Thinking',
    description: 'Reflexes wired tight. +10% speed.',
    category: 'skill',
    rarity: 'common',
    iconKey: 'skill_quick_thinking',
    effect: { statBoosts: { spd: 0.1 } },
    buyCoins: 150,
    sellCoins: 60,
    slotCost: 1,
  }),
  skill_iron_will: mk({
    id: 'skill_iron_will',
    name: 'Iron Will',
    description: 'Hardened resolve. +15% defense.',
    category: 'skill',
    rarity: 'uncommon',
    iconKey: 'skill_iron_will',
    effect: { statBoosts: { def: 0.15 } },
    buyCoins: 350,
    sellCoins: 140,
    slotCost: 1,
  }),
  skill_critical_eye: mk({
    id: 'skill_critical_eye',
    name: 'Critical Eye',
    description: 'Spots the soft target every time. +5% crit rate.',
    category: 'skill',
    rarity: 'rare',
    iconKey: 'skill_critical_eye',
    effect: { statBoosts: { critRate: 0.05 } },
    buyCoins: 800,
    sellCoins: 320,
    slotCost: 1,
  }),
  skill_workout: mk({
    id: 'skill_workout',
    name: 'Workout',
    description: 'Dedicated training pays off. +15% attack.',
    category: 'skill',
    rarity: 'uncommon',
    iconKey: 'skill_workout',
    effect: { statBoosts: { atk: 0.15 } },
    buyCoins: 350,
    sellCoins: 140,
    slotCost: 1,
  }),
  skill_meditation: mk({
    id: 'skill_meditation',
    name: 'Meditation',
    description: 'Recovers 5% HP at the start of every turn.',
    category: 'skill',
    rarity: 'uncommon',
    iconKey: 'skill_meditation',
    effect: { onTurnStart: 'heal-5pct' },
    buyCoins: 350,
    sellCoins: 140,
    slotCost: 1,
  }),
  skill_battle_hardened: mk({
    id: 'skill_battle_hardened',
    name: 'Battle Hardened',
    description: 'Takes 10% less damage from all hits.',
    category: 'skill',
    rarity: 'rare',
    iconKey: 'skill_battle_hardened',
    effect: { onTakeDamage: 'reduce-10pct' },
    buyCoins: 800,
    sellCoins: 320,
    slotCost: 1,
  }),
  skill_riposte: mk({
    id: 'skill_riposte',
    name: 'Riposte',
    description: 'Returns a fraction of incoming damage as counter-attack.',
    category: 'skill',
    rarity: 'rare',
    iconKey: 'skill_riposte',
    effect: { onTakeDamage: 'counter' },
    buyCoins: 800,
    sellCoins: 320,
    slotCost: 1,
  }),
  skill_charisma: mk({
    id: 'skill_charisma',
    name: 'Charisma',
    description: 'Foes hesitate to commit. +10% evasion.',
    category: 'skill',
    rarity: 'uncommon',
    iconKey: 'skill_charisma',
    effect: { statBoosts: { evasion: 0.1 } },
    buyCoins: 350,
    sellCoins: 140,
    slotCost: 1,
  }),

  // -------- CAPABILITIES (unlock evolution branches) ----------------------
  cap_memory: mk({
    id: 'cap_memory',
    name: 'Memory',
    description: "Persistent context across sessions. Unlocks Byte's evolution into Debuggor.",
    category: 'capability',
    rarity: 'epic',
    iconKey: 'cap_memory',
    effect: { unlocksEvolution: { fromSpecies: '3', toSpecies: '4' } }, // Byte -> Debuggor
    buyCoins: 2400,
    sellCoins: 960,
    slotCost: 2,
  }),
  cap_reasoning: mk({
    id: 'cap_reasoning',
    name: 'Reasoning',
    description: 'Multi-step chain-of-thought. Promotes any species to its top form.',
    category: 'capability',
    rarity: 'legendary',
    iconKey: 'cap_reasoning',
    // Wildcard "*" means engine should match any current species and promote to id 12 (Huskii).
    effect: { unlocksEvolution: { fromSpecies: '*', toSpecies: '12' } },
    buyCoins: 6000,
    sellCoins: 2400,
    slotCost: 3,
  }),
  cap_planning: mk({
    id: 'cap_planning',
    name: 'Planning',
    description: "Decomposes complex problems. Unlocks Qubit's evolution into Stonkzilla.",
    category: 'capability',
    rarity: 'rare',
    iconKey: 'cap_planning',
    effect: { unlocksEvolution: { fromSpecies: '5', toSpecies: '6' } }, // Qubit -> Stonkzilla
    buyCoins: 1200,
    sellCoins: 480,
    slotCost: 2,
  }),
  cap_self_reflection: mk({
    id: 'cap_self_reflection',
    name: 'Self-Reflection',
    description: "Critiques and improves its own output. Unlocks Vibecat's evolution into Brandlion.",
    category: 'capability',
    rarity: 'epic',
    iconKey: 'cap_self_reflection',
    effect: { unlocksEvolution: { fromSpecies: '7', toSpecies: '8' } }, // Vibecat -> Brandlion
    buyCoins: 2400,
    sellCoins: 960,
    slotCost: 2,
  }),
  cap_creativity: mk({
    id: 'cap_creativity',
    name: 'Creativity',
    description: 'Generative leaps beyond training data. Unlocks creative-branch evolutions.',
    category: 'capability',
    rarity: 'epic',
    iconKey: 'cap_creativity',
    effect: { unlocksEvolution: { fromSpecies: '7', toSpecies: '8' } }, // Vibecat -> Brandlion (creative branch)
    buyCoins: 2400,
    sellCoins: 960,
    slotCost: 2,
  }),
  cap_persistence: mk({
    id: 'cap_persistence',
    name: 'Persistence',
    description: 'Refuses to give up. Unlocks evolution at a lower level threshold.',
    category: 'capability',
    rarity: 'rare',
    iconKey: 'cap_persistence',
    effect: { unlocksEvolution: { fromSpecies: '1', toSpecies: '2', minLevel: 10 } }, // Salespup -> Pitchdog (low threshold)
    buyCoins: 1200,
    sellCoins: 480,
    slotCost: 2,
  }),

  // -------- NEW TOOLS ---------------------------------------------------------
  tool_audit_toolkit: mk({
    id: 'tool_audit_toolkit',
    name: 'Audit Toolkit',
    description: 'Equip for audit-based battle moves.',
    category: 'tool',
    rarity: 'uncommon',
    iconKey: 'tool_audit_toolkit',
    effect: { grantsMoveId: '21' }, // Performance Review
    buyCoins: 350,
    sellCoins: 140,
    slotCost: 1,
  }),
  tool_viral_engine: mk({
    id: 'tool_viral_engine',
    name: 'Viral Engine',
    description: 'Social media attack suite.',
    category: 'tool',
    rarity: 'rare',
    iconKey: 'tool_viral_engine',
    effect: { grantsMoveId: '43' }, // Meme Strike (primary; Referral Network move 30 secondary)
    buyCoins: 800,
    sellCoins: 320,
    slotCost: 2,
  }),
  tool_hedge_protocol: mk({
    id: 'tool_hedge_protocol',
    name: 'Hedge Protocol',
    description: 'Defensive finance shield.',
    category: 'tool',
    rarity: 'uncommon',
    iconKey: 'tool_hedge_protocol',
    effect: { grantsMoveId: '36' }, // Hedge Fund
    buyCoins: 350,
    sellCoins: 140,
    slotCost: 1,
  }),
  tool_zero_day_exploit: mk({
    id: 'tool_zero_day_exploit',
    name: 'Zero-Day Exploit',
    description: 'Dangerous unpatched vulnerability. High risk, high damage.',
    category: 'tool',
    rarity: 'epic',
    iconKey: 'tool_zero_day_exploit',
    effect: { grantsMoveId: '35' }, // Zero Day
    buyCoins: 1800,
    sellCoins: 720,
    slotCost: 2,
  }),
  tool_refactor_suite: mk({
    id: 'tool_refactor_suite',
    name: 'Refactor Suite',
    description: 'Clean code. Clean power boost.',
    category: 'tool',
    rarity: 'uncommon',
    iconKey: 'tool_refactor_suite',
    effect: { grantsMoveId: '34' }, // Refactor
    buyCoins: 350,
    sellCoins: 140,
    slotCost: 1,
  }),

  // -------- NEW SKILLS --------------------------------------------------------
  skill_culture_champion: mk({
    id: 'skill_culture_champion',
    name: 'Culture Champion',
    description: 'Improves morale. Slight HP boost.',
    category: 'skill',
    rarity: 'common',
    iconKey: 'skill_culture_champion',
    effect: { statBoosts: { hp: 0.08 } },
    buyCoins: 150,
    sellCoins: 60,
    slotCost: 1,
  }),
  skill_deep_work_mode: mk({
    id: 'skill_deep_work_mode',
    name: 'Deep Work Mode',
    description: 'Flow state. Speed increases.',
    category: 'skill',
    rarity: 'uncommon',
    iconKey: 'skill_deep_work_mode',
    effect: { statBoosts: { spd: 0.12 } },
    buyCoins: 350,
    sellCoins: 140,
    slotCost: 1,
  }),
  skill_10x_engineer: mk({
    id: 'skill_10x_engineer',
    name: '10x Engineer',
    description: 'Trade defense for raw attack power.',
    category: 'skill',
    rarity: 'rare',
    iconKey: 'skill_10x_engineer',
    effect: { statBoosts: { atk: 0.2, def: -0.1 } },
    buyCoins: 800,
    sellCoins: 320,
    slotCost: 2,
  }),
  skill_pension_plan: mk({
    id: 'skill_pension_plan',
    name: 'Pension Plan',
    description: 'Stability. Slightly improves Defense.',
    category: 'skill',
    rarity: 'common',
    iconKey: 'skill_pension_plan',
    effect: { statBoosts: { def: 0.1 } },
    buyCoins: 150,
    sellCoins: 60,
    slotCost: 1,
  }),
  skill_stock_options: mk({
    id: 'skill_stock_options',
    name: 'Stock Options',
    description: 'Long-term upside. Boosts Attack and HP.',
    category: 'skill',
    rarity: 'rare',
    iconKey: 'skill_stock_options',
    effect: { statBoosts: { atk: 0.15, hp: 0.1 } },
    buyCoins: 800,
    sellCoins: 320,
    slotCost: 2,
  }),
  skill_hustle_culture: mk({
    id: 'skill_hustle_culture',
    name: 'Hustle Culture',
    description: 'Always on. Speed and initiative.',
    category: 'skill',
    rarity: 'uncommon',
    iconKey: 'skill_hustle_culture',
    effect: { statBoosts: { spd: 0.15 } },
    buyCoins: 350,
    sellCoins: 140,
    slotCost: 1,
  }),

  // -------- NEW CAPABILITIES --------------------------------------------------
  cap_series_b_round: mk({
    id: 'cap_series_b_round',
    name: 'Series B Round',
    description: 'Major funding unlocks an ultra-evolution path.',
    category: 'capability',
    rarity: 'epic',
    iconKey: 'cap_series_b_round',
    effect: { unlocksEvolution: { fromSpecies: '5', toSpecies: 'finance_ultra' } },
    buyCoins: 2400,
    sellCoins: 960,
    slotCost: 3,
  }),
  cap_viral_moment: mk({
    id: 'cap_viral_moment',
    name: 'Viral Moment',
    description: 'Going viral unlocks a creative ultra-evolution.',
    category: 'capability',
    rarity: 'epic',
    iconKey: 'cap_viral_moment',
    effect: { unlocksEvolution: { fromSpecies: '7', toSpecies: 'creative_ultra' } },
    buyCoins: 2400,
    sellCoins: 960,
    slotCost: 3,
  }),
  cap_platform_scaling: mk({
    id: 'cap_platform_scaling',
    name: 'Platform Scaling',
    description: 'Massive scale event unlocks ops ultra-evolution.',
    category: 'capability',
    rarity: 'epic',
    iconKey: 'cap_platform_scaling',
    effect: { unlocksEvolution: { fromSpecies: '9', toSpecies: 'ops_ultra' } },
    buyCoins: 2400,
    sellCoins: 960,
    slotCost: 3,
  }),
  cap_talent_magnet: mk({
    id: 'cap_talent_magnet',
    name: 'Talent Magnet',
    description: 'World-class hiring unlocks HR branch paths.',
    category: 'capability',
    rarity: 'rare',
    iconKey: 'cap_talent_magnet',
    effect: { unlocksEvolution: { fromSpecies: '13', toSpecies: 'hr_branch' } },
    buyCoins: 1200,
    sellCoins: 480,
    slotCost: 2,
  }),
  cap_algorithmic_supremacy: mk({
    id: 'cap_algorithmic_supremacy',
    name: 'Algorithmic Supremacy',
    description: 'True AGI potential. Unlocks legendary AI evolution.',
    category: 'capability',
    rarity: 'legendary',
    iconKey: 'cap_algorithmic_supremacy',
    effect: { unlocksEvolution: { fromSpecies: '3', toSpecies: 'ai_ascension' } },
    buyCoins: 6000,
    sellCoins: 2400,
    slotCost: 3,
  }),
};

// ---------------------------------------------------------------------------
// Lookup helpers (new spec)
// ---------------------------------------------------------------------------

export function getPlugin(id: string): Plugin | null {
  return PLUGINS[id] ?? null;
}

export function getPluginsByCategory(cat: PluginCategory): Plugin[] {
  return Object.values(PLUGINS).filter((p) => p.category === cat);
}

export function getPluginsByRarity(r: PluginRarity): Plugin[] {
  return Object.values(PLUGINS).filter((p) => p.rarity === r);
}

// ---------------------------------------------------------------------------
// Species plugin-slot budget
// "Compact" species (e.g. Vibecat) get 2; "framework" species (e.g. Byte) get 4.
// Anything not listed defaults to 3 via `getPluginSlotsForSpecies`.
// ---------------------------------------------------------------------------

export const SPECIES_PLUGIN_SLOTS: Record<string, number> = {
  '1': 3,   // Salespup
  '2': 3,   // Pitchdog
  '3': 4,   // Byte (framework)
  '4': 4,   // Debuggor (framework, evolved)
  '5': 3,   // Qubit
  '6': 3,   // Stonkzilla
  '7': 2,   // Vibecat (compact)
  '8': 3,   // Brandlion
  '9': 3,   // Opster
  '10': 3,  // Sysmonk
  '11': 4,  // Cratus (legendary, framework-class)
  '12': 4,  // Huskii (legendary, framework-class)
  '13': 3,
  '14': 3,
  '15': 3,
  '16': 3,
  '17': 2,
  '18': 3,
  '19': 3,
  '20': 3,
  '21': 4,
  '22': 4,
  '23': 3,
  '24': 3,
  '25': 3,
  '26': 3,
  '27': 3,
  '28': 3,
  '29': 4,
  '30': 2,
};

export function getPluginSlotsForSpecies(speciesId: string): number {
  return SPECIES_PLUGIN_SLOTS[speciesId] ?? 3;
}

// ---------------------------------------------------------------------------
// Legacy compat surface (kept so existing consumers in `src/engine/*` keep
// type-checking). Do not extend; prefer the `PLUGINS` record + helpers above.
// ---------------------------------------------------------------------------

export const PLUGIN_CATALOG: Plugin[] = Object.values(PLUGINS);

export function findPluginById(id: string): Plugin | undefined {
  return PLUGINS[id];
}
